/**
 * DIEUKYDIEU.TV - MODERN WEB STORY MODULE
 * Handles fullscreen 3-step immersive stories for provinces with top progress bars,
 * ambient dynamic desktop backdrop, floating desktop arrows, and direct CTA answer linking.
 * Under 300 lines rule compliant.
 */

import { t } from './i18n.js';
import { state } from './state.js';
import { storyApi } from './api/story.api.js';
import { openStoryDetail } from './story.js';

const PROVINCES_ORDER = ['hue', 'danang', 'binhdinh', 'vungtau', 'bentre'];
let activeStory = null;
let currentSlideIndex = 0;
let slideProgressInterval = null;
let slideProgressPct = 0;
const SLIDE_DURATION_MS = 6000;
let isPaused = false;

function getStoryDialog() {
    let dialog = document.getElementById('web-story-modal');
    if (!dialog) {
        dialog = document.createElement('dialog');
        dialog.id = 'web-story-modal';
        dialog.className = 'web-story-dialog';
        dialog.setAttribute('closedby', 'any');
        document.body.appendChild(dialog);
    }
    return dialog;
}

export async function openWebStory(storyId) {
    if (!storyId) return;
    try {
        const lang = state.currentLang || 'vi';
        const data = await storyApi.getStoryById(storyId, lang);
        if (!data || !data.slides || data.slides.length === 0) return;

        activeStory = data;
        currentSlideIndex = 0;

        const dialog = getStoryDialog();
        if (activeStory.colorRGB) {
            dialog.style.setProperty('--ws-rgb', activeStory.colorRGB);
        } else {
            dialog.style.removeProperty('--ws-rgb');
        }
        renderStoryDOM(dialog);

        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');

        document.documentElement.classList.add('ws-scroll-lock');
        attachStoryEvents(dialog);
        goToSlide(0);
        storyApi.trackStoryEvent(storyId, 'view').catch(() => {});
    } catch (err) {
        console.error('[webStory] Error opening story:', err);
    }
}

export function closeWebStory() {
    stopSlideTimers();
    const dialog = document.getElementById('web-story-modal');
    if (dialog && dialog.open) dialog.close();
    document.documentElement.classList.remove('ws-scroll-lock');
    activeStory = null;
    currentSlideIndex = 0;
}

function renderStoryDOM(dialog) {
    if (!activeStory) return;
    const progressBarsHTML = activeStory.slides
        .map((_, i) => `<div class="ws-progress-bar" id="ws-bar-${i}"><div class="ws-progress-fill"></div></div>`)
        .join('');

    dialog.innerHTML = `
        <button class="ws-desktop-arrow ws-arrow-prev liquid-glass" id="ws-desktop-prev" aria-label="${t('story_prev_aria') || 'Slide trước'}" title="Slide trước">
            <span class="material-symbols-rounded">chevron_left</span>
        </button>
        <button class="ws-desktop-arrow ws-arrow-next liquid-glass" id="ws-desktop-next" aria-label="${t('story_next_aria') || 'Slide sau'}" title="Slide sau">
            <span class="material-symbols-rounded">chevron_right</span>
        </button>

        <div class="web-story-frame" id="ws-frame">
            <div class="ws-slide-bg" id="ws-bg-scene"></div>

            <div class="ws-top-controls">
                <div class="ws-progress-track">${progressBarsHTML}</div>
                <div class="ws-meta-row">
                    <div class="ws-location-chip liquid-glass">
                        <span class="ws-location-dot"></span>
                        <span>${(state.currentLang === 'vi' && activeStory.name) ? activeStory.name : (t(activeStory.key) || activeStory.name)}</span>
                    </div>
                    <button class="ws-close-btn liquid-glass" id="ws-close-btn" aria-label="${t('story_close_aria') || 'Đóng'}" title="${t('quiz_close_aria') || 'Đóng'} (ESC)">
                        <span class="material-symbols-rounded">close</span>
                        <kbd class="ws-kbd-badge">ESC</kbd>
                    </button>
                </div>
            </div>

            <div class="ws-tap-zone ws-tap-left" id="ws-tap-left" aria-label="${t('story_prev_aria') || 'Trang trước'}"></div>
            <div class="ws-tap-zone ws-tap-right" id="ws-tap-right" aria-label="${t('story_next_aria') || 'Trang kế tiếp'}"></div>

            <div class="ws-content-container" id="ws-content-box">
                <div class="ws-content-card">
                    <div class="ws-kicker-badge" id="ws-kicker">
                        <span class="material-symbols-rounded" id="ws-kicker-icon">auto_awesome</span>
                        <span id="ws-kicker-text"></span>
                    </div>
                    <h2 class="ws-title" id="ws-title"></h2>
                    <p class="ws-description" id="ws-desc"></p>
                    <div class="ws-answer-card" id="ws-answer-card">
                        <a class="web-story-answer-btn" id="ws-answer-btn" href="https://mb.dkn.tv/bai-viet-cua-dai-su-ly-hong-chi/nha-sang-lap-phap-luan-cong-cong-bo-bai-viet-vi-sao-co-nhan-loai.html" target="_blank" rel="noopener noreferrer">
                            <span class="material-symbols-rounded ws-sparkle-icon">auto_awesome</span>
                            <span id="ws-answer-btn-text">${t('story_answer_btn') || 'ĐÁP ÁN: VÌ SAO CÓ NHÂN LOẠI'}</span>
                            <span class="material-symbols-rounded">open_in_new</span>
                        </a>
                    </div>
                    <div class="ws-tap-hint" id="ws-tap-hint">
                        <span class="material-symbols-rounded">touch_app</span>
                        <span>Chạm để chuyển tiếp • Nhấn ESC hoặc nút Đóng để thoát</span>
                    </div>
                </div>
            </div>
        </div>
    `;
}

function goToSlide(index) {
    if (!activeStory || !activeStory.slides[index]) return;
    currentSlideIndex = index;
    const slide = activeStory.slides[index];

    const bgScene = document.getElementById('ws-bg-scene');
    const ambientBackdrop = document.getElementById('ws-ambient-backdrop');
    if (slide.bgImg) {
        if (bgScene) bgScene.style.backgroundImage = `url('${slide.bgImg}')`;
        if (ambientBackdrop) ambientBackdrop.style.backgroundImage = `url('${slide.bgImg}')`;
    }

    const kickerText = document.getElementById('ws-kicker-text');
    const kickerIcon = document.getElementById('ws-kicker-icon');
    const titleEl = document.getElementById('ws-title');
    const descEl = document.getElementById('ws-desc');
    const answerCard = document.getElementById('ws-answer-card');
    const tapHint = document.getElementById('ws-tap-hint');

    if (kickerText) kickerText.textContent = slide.kicker || activeStory.prompt;
    if (kickerIcon) kickerIcon.textContent = slide.badgeIcon || 'sparkles';
    if (titleEl) titleEl.textContent = slide.title || '';
    if (descEl) descEl.textContent = slide.desc || '';

    const isAnswerSlide = slide.type === 'answer' || index === activeStory.slides.length - 1;
    if (answerCard) answerCard.style.display = isAnswerSlide ? 'block' : 'none';
    if (tapHint) tapHint.style.display = isAnswerSlide ? 'none' : 'flex';

    updateProgressBars(index);
    startSlideTimer();
}

function updateProgressBars(activeIndex) {
    if (!activeStory) return;
    activeStory.slides.forEach((_, i) => {
        const bar = document.getElementById(`ws-bar-${i}`);
        if (!bar) return;
        const fill = bar.querySelector('.ws-progress-fill');
        bar.classList.toggle('completed', i < activeIndex);
        if (fill) fill.style.width = i < activeIndex ? '100%' : '0%';
    });
}

function startSlideTimer() {
    stopSlideTimers();
    slideProgressPct = 0;
    const stepInterval = 50;
    const totalSteps = SLIDE_DURATION_MS / stepInterval;

    slideProgressInterval = setInterval(() => {
        if (isPaused) return;
        slideProgressPct += (100 / totalSteps);
        const curBar = document.getElementById(`ws-bar-${currentSlideIndex}`);
        if (curBar) {
            const fill = curBar.querySelector('.ws-progress-fill');
            if (fill) fill.style.width = `${Math.min(100, slideProgressPct)}%`;
        }
        if (slideProgressPct >= 100) {
            stopSlideTimers();
            nextWebStorySlide();
        }
    }, stepInterval);
}

function stopSlideTimers() {
    if (slideProgressInterval) {
        clearInterval(slideProgressInterval);
        slideProgressInterval = null;
    }
}

export function nextWebStorySlide() {
    if (!activeStory) return;
    if (currentSlideIndex < activeStory.slides.length - 1) {
        goToSlide(currentSlideIndex + 1);
    } else {
        const curOrderIdx = PROVINCES_ORDER.indexOf(activeStory.id);
        if (curOrderIdx >= 0 && curOrderIdx < PROVINCES_ORDER.length - 1) {
            openWebStory(PROVINCES_ORDER[curOrderIdx + 1]);
        } else {
            closeWebStory();
        }
    }
}

export function prevWebStorySlide() {
    if (!activeStory) return;
    if (currentSlideIndex > 0) {
        goToSlide(currentSlideIndex - 1);
    } else {
        const curOrderIdx = PROVINCES_ORDER.indexOf(activeStory.id);
        if (curOrderIdx > 0) openWebStory(PROVINCES_ORDER[curOrderIdx - 1]);
        else goToSlide(0);
    }
}

function attachStoryEvents(dialog) {
    const closeBtn = document.getElementById('ws-close-btn');
    if (closeBtn) closeBtn.onclick = (e) => { e.stopPropagation(); closeWebStory(); };

    const desktopClose = document.getElementById('ws-desktop-close-btn');
    if (desktopClose) desktopClose.onclick = (e) => { e.stopPropagation(); closeWebStory(); };

    const tapLeft = document.getElementById('ws-tap-left');
    if (tapLeft) tapLeft.onclick = (e) => { e.stopPropagation(); prevWebStorySlide(); };

    const tapRight = document.getElementById('ws-tap-right');
    if (tapRight) tapRight.onclick = (e) => { e.stopPropagation(); nextWebStorySlide(); };

    const desktopPrev = document.getElementById('ws-desktop-prev');
    if (desktopPrev) desktopPrev.onclick = (e) => { e.stopPropagation(); prevWebStorySlide(); };

    const desktopNext = document.getElementById('ws-desktop-next');
    if (desktopNext) desktopNext.onclick = (e) => { e.stopPropagation(); nextWebStorySlide(); };

    const answerBtn = document.getElementById('ws-answer-btn');
    if (answerBtn) {
        answerBtn.onclick = (e) => {
            const targetUrl = (activeStory && activeStory.targetUrl) || 
                'https://mb.dkn.tv/bai-viet-cua-dai-su-ly-hong-chi/nha-sang-lap-phap-luan-cong-cong-bo-bai-viet-vi-sao-co-nhan-loai.html';
            storyApi.trackStoryEvent(activeStory?.id, 'answer_click').catch(() => {});
            closeWebStory();
            window.open(targetUrl, '_blank', 'noopener,noreferrer');
            e.preventDefault();
        };
    }

    const frame = document.getElementById('ws-frame');
    if (frame) {
        frame.onpointerdown = () => { isPaused = true; };
        frame.onpointerup = () => { isPaused = false; };
        frame.onpointercancel = () => { isPaused = false; };
    }

    dialog.onclick = (e) => {
        if (e.target === dialog) closeWebStory();
    };

    dialog.onkeydown = (e) => {
        if (e.key === 'Escape') closeWebStory();
        else if (e.key === 'ArrowRight' || e.key === ' ') nextWebStorySlide();
        else if (e.key === 'ArrowLeft') prevWebStorySlide();
    };
}
